/**
 * Posteingang der angemeldeten Person (Spec §15).
 *
 * Anders als bei Features gibt es hier bewusst EINEN gemeinsamen Zwischenspeicher:
 * Die Glocke in der Kopfleiste und das Panel darunter zeigen dieselbe Liste, und
 * eine zweite Quelle dafür hieße nur, dass die Zahl an der Glocke irgendwann etwas
 * anderes behauptet als die Einträge darunter.
 *
 * Abgefragt wird per Polling. Für ein Werkzeug, in dem Dinge über Stunden reifen,
 * ist eine Minute Verzögerung ohne Belang — und ein offener WebSocket kostet
 * Verbindungen, Wiederverbindungslogik und eine zweite Fehlerquelle, die niemand
 * bemerkt, wenn sie ausfällt.
 */

import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { NotificationDto, NotificationListDto } from '@pmpro/shared';
import { api } from '../api/client';

/** Wie oft nachgefragt wird, solange der Tab sichtbar ist. */
const ABRUF_INTERVALL_MS = 60_000;

export const useNotificationStore = defineStore('notifications', () => {
  const items = ref<NotificationDto[]>([]);
  const unread = ref(0);
  const laedt = ref(false);
  const fehler = ref('');

  let timer: ReturnType<typeof setInterval> | null = null;

  const hatUngelesene = computed(() => unread.value > 0);

  async function laden(): Promise<void> {
    laedt.value = true;
    try {
      const antwort = await api.get<NotificationListDto>('/api/notifications');
      items.value = antwort.items;
      unread.value = antwort.unread;
      fehler.value = '';
    } catch {
      // Ein fehlgeschlagener Hintergrundabruf ist kein Grund, den Bildschirm mit
      // einer Fehlermeldung zu unterbrechen — die nächste Runde kommt bestimmt.
      fehler.value = 'Die Hinweise konnten nicht geladen werden.';
    } finally {
      laedt.value = false;
    }
  }

  async function alsGelesen(id: string): Promise<void> {
    const vorhanden = items.value.find((hinweis) => hinweis.id === id);
    if (!vorhanden || vorhanden.readAt) return;

    // Sofort umschalten, damit der Klick sich unmittelbar anfühlt; beim Fehlschlag
    // holt der nächste Abruf den echten Stand zurück.
    vorhanden.readAt = new Date().toISOString();
    unread.value = Math.max(0, unread.value - 1);

    try {
      await api.post(`/api/notifications/${id}/read`);
    } catch {
      await laden();
    }
  }

  async function allesGelesen(): Promise<void> {
    const jetzt = new Date().toISOString();
    for (const hinweis of items.value) {
      if (!hinweis.readAt) hinweis.readAt = jetzt;
    }
    unread.value = 0;

    try {
      await api.post('/api/notifications/read-all');
    } catch {
      await laden();
    }
  }

  function starteAbruf(): void {
    if (timer) return;
    void laden();
    timer = setInterval(() => {
      // Im Hintergrundtab fragt niemand nach — das spart Anfragen, ohne dass es
      // jemandem auffällt: Beim Zurückwechseln lädt `sichtbarkeitGeaendert` neu.
      if (document.visibilityState === 'visible') void laden();
    }, ABRUF_INTERVALL_MS);
    document.addEventListener('visibilitychange', sichtbarkeitGeaendert);
  }

  function beendeAbruf(): void {
    if (timer) clearInterval(timer);
    timer = null;
    document.removeEventListener('visibilitychange', sichtbarkeitGeaendert);
  }

  function sichtbarkeitGeaendert(): void {
    if (document.visibilityState === 'visible') void laden();
  }

  return {
    items,
    unread,
    laedt,
    fehler,
    hatUngelesene,
    laden,
    alsGelesen,
    allesGelesen,
    starteAbruf,
    beendeAbruf
  };
});
